import { StateError } from '../errors';
import { HEX_DIRS, hexCornerVertices, vertexHexes, vertexNeighbors, type HexCoord } from './axial';
import type { HarborDir, MapDefJson, MapValidationEnv } from './mapSchema';

// Sea-map validation (H20-S1, T-SEA-004): the semantic checks a map with a `sea` block must
// pass on top of the shape. Every refusal names its reason — a footprint author (or a paste)
// is told WHAT is wrong, never just "invalid". Pure; board/ only.
//
// The vocabulary: a LAND-CAPABLE cell is a `hexes` entry (a fixed land tile, or a `random`
// position that the deal may make land OR water); a WATER cell is fixed open water (the frame
// ring among them); a FOG cell is unexplored until play reveals it.

/** The reserved pool key a sea map may deal INSTEAD of a tile: the cell becomes open water. */
export const WATER_POOL_KEY = 'water';

const key = (c: HexCoord): string => `${c.q},${c.r}`;
const DIR_INDEX: Readonly<Record<HarborDir, number>> = { E: 0, NE: 1, NW: 2, W: 3, SW: 4, SE: 5 };
const neighbor = (c: HexCoord, dir: HarborDir): HexCoord => {
  const d = HEX_DIRS[DIR_INDEX[dir]]!;
  return { q: c.q + d.q, r: c.r + d.r };
};
const neighbors = (c: HexCoord): HexCoord[] => HEX_DIRS.map((d) => ({ q: c.q + d.q, r: c.r + d.r }));

function takesDisc(env: MapValidationEnv, tileId: string): boolean {
  const t = env.tiles.get(tileId);
  return t !== undefined && (t.produces !== null || t.numbered === true);
}

/** A pool of tile counts: known tiles (and the reserved water key) only; returns the disc-taking total. */
function checkTilePool(pool: Readonly<Record<string, number>>, env: MapValidationEnv, where: string): { size: number; numbered: number } {
  let size = 0;
  let numbered = 0;
  for (const [tileId, n] of Object.entries(pool)) {
    if (tileId !== WATER_POOL_KEY && !env.tiles.has(tileId)) throw new StateError(`map rejected: unknown tile '${tileId}' in ${where}`);
    size += n;
    if (tileId !== WATER_POOL_KEY && takesDisc(env, tileId)) numbered += n;
  }
  return { size, numbered };
}

export function validateSeaMap(def: MapDefJson, env: MapValidationEnv, landKeys: ReadonlySet<string>): void {
  const sea = def.sea!;
  const waterKeys = new Set<string>();
  const frameKeys = new Set<string>();
  for (const w of sea.waters) {
    const k = key(w);
    if (waterKeys.has(k) || landKeys.has(k)) throw new StateError(`map rejected: cell ${k} is listed in two domains`);
    waterKeys.add(k);
    if (w.frame === true) frameKeys.add(k);
  }
  const fogKeys = new Set<string>();
  for (const f of sea.fog?.cells ?? []) {
    const k = key(f);
    if (fogKeys.has(k) || landKeys.has(k) || waterKeys.has(k)) throw new StateError(`map rejected: cell ${k} is listed in two domains`);
    fogKeys.add(k);
  }
  const isCell = (c: HexCoord): boolean => landKeys.has(key(c)) || waterKeys.has(key(c)) || fogKeys.has(key(c));
  const total = landKeys.size + waterKeys.size + fogKeys.size;
  if (total > env.maxHexes) throw new StateError(`map rejected: ${total} cells exceeds the ${env.maxHexes}-cell size cap`);

  // land (and anything that may become land) is always surrounded by cells: a sea map has no
  // coast onto nothing — every shore faces water the ships and harbours can use
  for (const h of def.hexes) {
    for (const n of neighbors(h)) if (!isCell(n)) throw new StateError(`map rejected: land at ${key(h)} touches the edge of the map`);
  }
  for (const f of sea.fog?.cells ?? []) {
    for (const n of neighbors(f)) if (!isCell(n)) throw new StateError(`map rejected: fog at ${key(f)} touches the edge of the map`);
  }

  // ── dealing: regions, pools, discs ──────────────────────────────────────────
  const regionOf = (h: { region?: string }): string => h.region ?? '';
  const randomByRegion = new Map<string, number>();
  const autoFixedByRegion = new Map<string, number>();
  let anyAuto = false;
  for (const h of def.hexes) {
    const r = regionOf(h);
    if (h.tile === 'random') {
      if (h.disc !== 'auto') throw new StateError(`map rejected: the random cell at ${key(h)} must take an 'auto' disc (it may be dealt water or a desert)`);
      randomByRegion.set(r, (randomByRegion.get(r) ?? 0) + 1);
      anyAuto = true;
    } else if (h.disc === 'auto') {
      if (!takesDisc(env, h.tile)) throw new StateError(`map rejected: an 'auto' disc on the disc-less tile '${h.tile}' at ${key(h)}`);
      autoFixedByRegion.set(r, (autoFixedByRegion.get(r) ?? 0) + 1);
      anyAuto = true;
    } else if (takesDisc(env, h.tile) && h.disc === null) {
      throw new StateError(`map rejected: missing disc on the numbered tile '${h.tile}' at ${key(h)}`);
    }
  }
  if (anyAuto && def.discPolicy !== 'random') throw new StateError("map rejected: a sea map that deals discs uses discPolicy 'random'");
  if (!anyAuto && def.discPolicy !== 'fixed') throw new StateError("map rejected: a fully fixed sea map uses discPolicy 'fixed'");
  if (def.randomPools?.tiles !== undefined || def.randomPools?.discs !== undefined) {
    throw new StateError('map rejected: a sea map deals from per-region pools (randomPools.regions)');
  }
  const regions = def.randomPools?.regions ?? {};
  const dealtRegions = new Set([...randomByRegion.keys(), ...autoFixedByRegion.keys()]);
  for (const r of Object.keys(regions)) {
    if (!dealtRegions.has(r)) throw new StateError(`map rejected: pool region '${r}' has no cells to deal to`);
  }
  for (const r of dealtRegions) {
    const pool = regions[r];
    if (!pool) throw new StateError(`map rejected: region '${r || '(unnamed)'}' has cells to deal but no pool`);
    const tiles = checkTilePool(pool.tiles, env, `region '${r}'`);
    const cells = randomByRegion.get(r) ?? 0;
    if (tiles.size !== cells) throw new StateError(`map rejected: region '${r}' pools ${tiles.size} tiles for ${cells} random cells`);
    const discsWanted = tiles.numbered + (autoFixedByRegion.get(r) ?? 0);
    if (pool.discs.length !== discsWanted) throw new StateError(`map rejected: region '${r}' pools ${pool.discs.length} discs for ${discsWanted} numbered cells`);
    if (pool.discs.some((d) => d === 7)) throw new StateError(`map rejected: 7 has no disc`);
  }

  // islets never touch the main island — generalised: land-capable cells of DIFFERENT regions
  // never share an edge, so what one region deals can never merge into another's island
  const landCell = new Map(def.hexes.map((h) => [key(h), h]));
  for (const h of def.hexes) {
    for (const n of neighbors(h)) {
      const other = landCell.get(key(n));
      if (other && regionOf(other) !== regionOf(h)) throw new StateError(`map rejected: regions '${regionOf(h)}' and '${regionOf(other)}' touch at ${key(h)}`);
    }
  }

  // ── fog ──────────────────────────────────────────────────────────────────────
  if (sea.fog) {
    if (sea.landfallVp > 0) throw new StateError('map rejected: Landfall and fog in one scenario (islands that grow are undefined in v1)');
    const tiles = checkTilePool(sea.fog.pool.tiles, env, 'the fog pool');
    if (tiles.size !== sea.fog.cells.length) throw new StateError(`map rejected: the fog pool holds ${tiles.size} tiles for ${sea.fog.cells.length} fog cells`);
    if (sea.fog.pool.discs.length !== tiles.numbered) throw new StateError(`map rejected: the fog pool holds ${sea.fog.pool.discs.length} discs for ${tiles.numbered} numbered tiles`);
  }

  // ── anchors: harbours, setup islands, starts, frame edges ───────────────────
  // CERTAIN land: a fixed land tile, or a random position whose region's pool holds no water —
  // the deal decides WHICH terrain lands there, never WHETHER it is land. An anchor on a cell the
  // deal might make water (an islet position) is refused: a harbour on open water, or a setup
  // island that sometimes is not there, is a map that works on some seeds only.
  const certainLand = (c: HexCoord): boolean => {
    const h = landCell.get(key(c));
    if (h === undefined) return false;
    if (h.tile !== 'random') return true;
    return (regions[regionOf(h)]?.tiles[WATER_POOL_KEY] ?? 0) === 0;
  };
  for (const h of def.harbors) {
    if (h.kind !== '3:1' && !env.resourceIds.has(h.kind)) throw new StateError(`map rejected: harbor kind '${h.kind}' is neither 3:1 nor a known resource`);
    if (!certainLand(h)) throw new StateError(`map rejected: harbor anchor h${h.q},${h.r} is not certain land`);
    const faced = neighbor(h, h.dir);
    if (!waterKeys.has(key(faced))) throw new StateError(`map rejected: harbor at h${h.q},${h.r} ${h.dir} does not face fixed water`);
  }
  for (const a of sea.setupIslands ?? []) {
    if (!certainLand(a)) throw new StateError(`map rejected: setup-island anchor ${key(a)} is not certain land`);
  }
  if (sea.pirateStart && !waterKeys.has(key(sea.pirateStart))) throw new StateError(`map rejected: the pirate starts on ${key(sea.pirateStart)}, which is not fixed water`);
  const bs = sea.banditStart;
  if (typeof bs === 'object' && 'q' in bs && !certainLand(bs)) throw new StateError(`map rejected: the bandit starts on ${key(bs)}, which is not certain land`);
  if (typeof bs === 'object' && 'disc' in bs) {
    const fixedHit = def.hexes.some((h) => h.disc === bs.disc);
    const pooled = Object.values(regions).some((p) => p.discs.includes(bs.disc));
    if (!fixedHit && !pooled) throw new StateError(`map rejected: the bandit starts on the ${bs.disc}, which no cell can hold`);
  }
  for (const e of sea.frameEdges) {
    if (!frameKeys.has(key(e)) || !frameKeys.has(key(neighbor(e, e.dir)))) {
      throw new StateError(`map rejected: frame edge ${key(e)} ${e.dir} is not between two frame cells`);
    }
  }
  for (const t of [...sea.constraints.forbidRedOn, ...(sea.constraints.poorNumbers?.tiles ?? [])]) {
    if (!env.tiles.has(t)) throw new StateError(`map rejected: layout constraint names unknown tile '${t}'`);
  }

  // ── fog out of setup's reach (Q21: engine-legal, but no shipped map allows it) ─────
  if (sea.fog && sea.setupDiscovery !== true) {
    const setupCells = setupLandCells(def, landCell);
    const touchesFog = (v: string): boolean => vertexHexes(v).some((c) => fogKeys.has(key(c)));
    for (const c of setupCells) {
      for (const v of hexCornerVertices(c.q, c.r)) {
        // a setup settlement on v, and the far end of its setup road or ship
        if (touchesFog(v) || vertexNeighbors(v).some(touchesFog)) {
          throw new StateError(`map rejected: fog is within reach of a setup placement at ${key(c)}`);
        }
      }
    }
  }

  for (const p of def.fixedPieces ?? []) {
    if (!env.pieceIds.has(p.piece)) throw new StateError(`map rejected: fixed piece '${p.piece}' is not a known piece id`);
    if (!p.vertex && !p.edge) throw new StateError(`map rejected: a fixed piece needs a vertex or edge site`);
    const site = p.vertex ?? p.edge!;
    if (!isCell(site)) throw new StateError(`map rejected: fixed piece site off the map at ${site.q},${site.r}`);
  }
}

/**
 * The land-capable cells a setup settlement may touch: the components (over land-capable
 * adjacency — a random cell might be dealt land) of the setup anchors, or every land-capable
 * cell when setup may use any island. Conservative on purpose: fog must be out of reach of
 * anything the deal COULD make settleable.
 */
function setupLandCells(def: MapDefJson, landCell: ReadonlyMap<string, { q: number; r: number }>): HexCoord[] {
  const anchors = def.sea!.setupIslands;
  if (anchors === null) return [...landCell.values()];
  const seen = new Set<string>();
  const out: HexCoord[] = [];
  const stack = anchors.map((a) => ({ q: a.q, r: a.r }));
  while (stack.length > 0) {
    const c = stack.pop()!;
    const k = key(c);
    if (seen.has(k) || !landCell.has(k)) continue;
    seen.add(k);
    out.push(c);
    for (const n of neighbors(c)) stack.push(n);
  }
  return out;
}
